import { APIProvider, Map, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { ReactNode, useEffect, useRef } from 'react';

import AppSettings from '../../constants';

import { mapStyles } from './mapStyles';

export interface Position {
    lat: number;
    lng: number;
}

interface Polyline {
    strokeWeight: number;
    strokeColor: string;
    path: Position[];
}

interface CloselinkMapProps {
    children?: ReactNode[];
    zoomControl?: boolean;
    defaultCenter?: Position;
    defaultZoom?: number;
    places?: Position[];
    polylines?: Polyline[];
    onDragEnd?: () => void;
    onDrag?: () => void;
    fitToBounds?: boolean;
    minZoom?: number;
    onApiIsLoaded?: () => void;
    center?: { lat: number; lng: number };
    zoom?: number;
    onCameraChanged?: (center: Position, zoom: number) => void;
}

interface MapContentProps {
    places?: Position[];
    defaultCenter?: Position;
    defaultZoom?: number;
    polylines?: Polyline[];
    fitToBounds?: boolean;
    onApiIsLoaded?: () => void;
    children?: ReactNode[];
}

const MapContent = ({
    places,
    defaultCenter = { lat: 0, lng: 0 },
    defaultZoom = 0,
    polylines,
    fitToBounds = false,
    onApiIsLoaded,
    children,
}: MapContentProps) => {
    const map = useMap();
    const mapsLibrary = useMapsLibrary('maps');
    const polylinesRef = useRef<google.maps.Polyline[]>([]);
    const resizeListenerRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        if (!map || !mapsLibrary) return;

        if (onApiIsLoaded) {
            onApiIsLoaded();
        }
    }, [map, mapsLibrary, onApiIsLoaded]);

    useEffect(() => {
        if (!map || !mapsLibrary) return;

        polylinesRef.current.forEach((polyline) => polyline.setMap(null));
        polylinesRef.current = [];

        polylines?.forEach((polyline) => {
            const mapPolyline = new google.maps.Polyline({
                path: polyline.path,
                strokeColor: polyline.strokeColor,
                strokeWeight: polyline.strokeWeight,
            });

            mapPolyline.setMap(map);
            polylinesRef.current.push(mapPolyline);
        });

        return () => {
            polylinesRef.current.forEach((polyline) => polyline.setMap(null));
        };
    }, [map, mapsLibrary, polylines]);

    useEffect(() => {
        if (!map || !mapsLibrary || !fitToBounds || !places) return;

        if (places.length === 1) {
            map.setCenter(new google.maps.LatLng(places[0].lat, places[0].lng));
            map.setZoom(7);
        } else if (places.length > 1) {
            const bounds = new google.maps.LatLngBounds();
            places.forEach((place) => {
                bounds.extend(new google.maps.LatLng(place.lat, place.lng));
            });
            map.fitBounds(bounds);
        } else {
            map.setCenter(new google.maps.LatLng(defaultCenter.lat, defaultCenter.lng));
            map.setZoom(defaultZoom);
        }

        const resizeHandler = () => {
            if (places.length === 1) {
                map.setCenter(new google.maps.LatLng(places[0].lat, places[0].lng));
                map.setZoom(7);
            } else if (places.length > 1) {
                const newBounds = new google.maps.LatLngBounds();
                places.forEach((place) => {
                    newBounds.extend(new google.maps.LatLng(place.lat, place.lng));
                });
                map.fitBounds(newBounds);
            }
        };

        if (resizeListenerRef.current) {
            window.removeEventListener('resize', resizeListenerRef.current);
        }
        resizeListenerRef.current = resizeHandler;
        window.addEventListener('resize', resizeHandler);

        return () => {
            if (resizeListenerRef.current) {
                window.removeEventListener('resize', resizeListenerRef.current);
            }
        };
    }, [map, mapsLibrary, places, fitToBounds, defaultCenter, defaultZoom]);

    return <>{children}</>;
};

export const CloselinkMap = (props: CloselinkMapProps) => {
    const {
        defaultCenter = { lat: 0, lng: 0 },
        defaultZoom = 0,
        zoomControl = false,
        onDrag,
        onDragEnd,
        minZoom = 0,
        center,
        zoom,
        places,
        children,
        polylines,
        fitToBounds = false,
        onApiIsLoaded,
        onCameraChanged,
    } = props;

    const handleCameraChanged = (ev: { detail: { center: Position; zoom: number } }) => {
        if (onCameraChanged) {
            onCameraChanged(ev.detail.center, ev.detail.zoom);
        }
    };

    return (
        <APIProvider apiKey={AppSettings.googleApiURLKey}>
            <Map
                defaultCenter={defaultCenter}
                defaultZoom={defaultZoom}
                center={fitToBounds ? undefined : center}
                zoom={fitToBounds ? undefined : zoom}
                disableDefaultUI={true}
                zoomControl={zoomControl}
                styles={mapStyles}
                minZoom={minZoom}
                restriction={{
                    latLngBounds: {
                        north: 85,
                        south: -85,
                        east: 180,
                        west: -180,
                    },
                    strictBounds: true,
                }}
                onDrag={onDrag}
                onDragend={onDragEnd}
                onCameraChanged={handleCameraChanged}
                style={{ width: '100%', height: '100%' }}
            >
                <MapContent
                    places={places}
                    defaultCenter={defaultCenter}
                    defaultZoom={defaultZoom}
                    polylines={polylines}
                    fitToBounds={fitToBounds}
                    onApiIsLoaded={onApiIsLoaded}
                >
                    {children}
                </MapContent>
            </Map>
        </APIProvider>
    );
};
